// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ChatMessage.test.tsx

/**
 * One message's Knowledge Database citations, in the two shapes it draws them.
 *
 * Beside the other citation helpers rather than inside `ChatMessage.tsx`: that
 * component was already well past the 400-line expert-legibility bar, and this
 * derivation is self-contained -- it reads a message's stored citations and
 * answers two questions about them, touching nothing else the component does.
 *
 * The two shapes are NOT interchangeable, which is the whole reason this is one
 * hook and not two calls at the call site:
 *
 * - `ruleCitations` is the deduped rule list. It is what the chips under the
 *   message are drawn and NUMBERED over.
 * - `claimLinks` is that same list as inline links in the prose, carrying the
 *   same numbers so a reader can tell which underlined phrase is which chip.
 *
 * Numbering and identity therefore come from the deduped list, while PROVENANCE
 * has to come from the RAW citations: `dedupeRuleCitations` drops every graph
 * record by design, and those records are what carry the chat or memory a claim
 * was discussed in. Deriving both here keeps that pairing in one place; reading
 * provenance off the deduped list was the first shape of this and every inline
 * link silently fell back to the registry.
 */

import { useMemo } from "react";

import {
  dedupeRuleCitations,
  ruleCitationKey,
  type TicketMessageCitation,
} from "./commentSourceUtils";
import {
  isRelatedCitation,
  useThreadCitationNumbersContext,
} from "./focusedCitedClaims";

import { claimCitationsFor } from "../../../components/ask-puntt-review/claimCitations";
import { type LinkableClaim } from "../../../plugins/rehype-claim-citation";

type RuleCitation = ReturnType<typeof dedupeRuleCitations>[number];

export interface MessageClaimCitations {
  /**
   * The rule citations, deduped on the visible quote, each with its number.
   * Filtered to the rule variant so only the fetch-free, Rules-tab-deep-linking
   * branch of `CommentCitationLinks` renders -- the file/record branches need
   * pane wiring and a follow-up fetch the chat message list does not have.
   * Deduped because different rules backed by the same evidence line would
   * otherwise render as duplicate chips. Sorted by number, so a source the
   * thread cited earlier leads with its lower number. Related rows are left
   * out: a chip is always a rule the answer cited.
   */
  ruleCitations: { citation: RuleCitation; n: number }[];
  /** The related rows left out of `ruleCitations`, counted, not numbered. */
  relatedCount: number;
  /**
   * The claims this answer QUOTES, as links in its own prose, carrying the
   * same numbers as the chips.
   */
  claimLinks: LinkableClaim[];
}

/**
 * Inside a thread that provides `ThreadCitationNumbersContext`, a source's
 * number is the thread's, so the same source reads as the same number in every
 * answer and on the rail. Outside one, a message numbers its own citations
 * from 1.
 */
export function useMessageClaimCitations(
  citations: readonly TicketMessageCitation[] | undefined,
): MessageClaimCitations {
  const threadNumbers = useThreadCitationNumbersContext();
  const deduped = useMemo(
    () => dedupeRuleCitations(citations ?? []),
    [citations],
  );

  const relatedCount = deduped.filter(isRelatedCitation).length;
  const ruleCitations = useMemo(() => {
    const numbered = deduped
      .filter((citation) => !isRelatedCitation(citation))
      .map((citation, index) => ({
        citation,
        n: threadNumbers?.get(ruleCitationKey(citation)) ?? index + 1,
      }));
    return threadNumbers ? numbered.sort((a, b) => a.n - b.n) : numbered;
  }, [deduped, threadNumbers]);

  const claimLinks = useMemo(() => {
    const links = claimCitationsFor({ citations: deduped }, citations ?? []);
    if (!threadNumbers) return links;
    return links.map((link) => {
      const n = link.citation
        ? threadNumbers.get(ruleCitationKey(link.citation as RuleCitation))
        : undefined;
      return n != null ? { ...link, number: n } : link;
    });
  }, [deduped, citations, threadNumbers]);

  return { ruleCitations, relatedCount, claimLinks };
}
